/**
 * Waveform — minimal canvas amplitude bar tied to the mic's RMS.
 *
 * Renders a horizontal bar that fills proportionally to the latest
 * mic level reading, EMA-smoothed so a single loud
 * sample doesn't yank the bar to 100%.
 *
 * Implementation note (the bug this file used to ship): the original
 * version drove the smoothed value through `useState` updated inside
 * `useEffect([rms])`. The level updates many times a second,
 * so every parent render scheduled another state update,
 * which scheduled another render, which fired the effect again — a
 * classic "Maximum update depth exceeded" tight loop.
 *
 * The fix: keep the EMA in a ref (no re-render on update), and flush
 * the latest value to a *throttled* state via requestAnimationFrame.
 * The screen sees a 60 fps animation max, decoupled from the 50 Hz
 * audio sample rate, and React's update-depth detector stays quiet.
 *
 * Spec: Docs/architecture/03-reasoning-layer.md S3.2 (waveform viz).
 */
import * as React from "react";
import { cn } from "@/lib/cn";

export interface WaveformProps {
  /** Latest mic level, range [0, 1]. */
  rms: number;
  /** Optional className override. */
  className?: string;
}

export function Waveform({ rms, className }: WaveformProps) {
  // EMA in a ref — updated on every render but never triggers one.
  const smoothedRef = React.useRef(0);
  // Throttled view of the EMA — drives the DOM bar width.
  const [pct, setPct] = React.useState(0);

  // Update the EMA on every render (cheap — primitive math, no DOM).
  smoothedRef.current = smoothedRef.current * 0.6 + rms * 0.4;

  // Schedule a rAF on each render that pushes the latest EMA to
  // state ONCE per frame. Successive renders within the same frame
  // coalesce because rAF callbacks for the same id are de-duped.
  React.useEffect(() => {
    let raf = 0;
    const tick = () => {
      const next = Math.min(100, Math.max(0, smoothedRef.current * 400));
      // Avoid an extra render when the rounded percent hasn't moved.
      setPct((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []); // run once

  return (
    <div
      role="meter"
      aria-label="microphone level"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        "h-2 w-32 overflow-hidden rounded-full bg-bg-elevated",
        className,
      )}
    >
      <div
        data-testid="waveform-bar"
        className="h-full rounded-full bg-accent transition-[width] duration-75"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
